import { all, put, spawn, takeEvery } from 'typed-redux-saga/macro';

import {
  I13N_ACTION_DOB_DEVICE_FLAGGED,
  I13N_ACTION_DOB_DEVICE_UNFLAGGED,
  I13N_CATEGORY_APP,
  I13N_TYPE_FEATURE_COUNT,
} from 'Lib/constants/i13nSpec';
import { ONE_DAY } from 'Lib/constants/time';
import getI13nUtils from 'Lib/i13n/getI13nUtils';
import { GetActionByType } from 'Lib/reduxType';
import { localStorage } from 'Lib/storage';
import { getDateFromTimeRemaining, getDaysLeft } from 'Lib/utils/dateTime';

import {
  deviceFlaggedForDobViolation,
  deviceUnflaggedForDobViolation,
  setDobViolationDaysLeft,
} from '../actions';
import { DEVICE_FLAGGED_FOR_DOB_VIOLATION, DEVICE_UNFLAGGED_FOR_DOB_VIOLATION } from '../constants';

const KEY_DOB_VIOLATION_BIRTHDATE = 'TinderWeb/dobViolationBirthdate';
const KEY_HAS_DOB_VIOLATION = 'TinderWeb/hasDobViolation';

const { executeEvent } = getI13nUtils();

export function* deviceFlaggedForDobViolationSaga(
  action: GetActionByType<typeof DEVICE_FLAGGED_FOR_DOB_VIOLATION>
) {
  // Birthday comes from onboarding input
  const { birthdate, ttl: ttlFromService = '', isAppealable } = action.payload;
  // TTL comes from server error response and is the time in ms until user is not underage
  const ttl = parseInt(ttlFromService || '', 10);

  executeEvent(I13N_TYPE_FEATURE_COUNT, {
    category: I13N_CATEGORY_APP,
    action: I13N_ACTION_DOB_DEVICE_FLAGGED,
    label: 'DOB Violation',
    message: 'Flagged',
  });

  // only persist if the ban is not appealable
  if (!isAppealable) {
    // Store the violation in local storage so that the device/session itself is flagged
    localStorage.setItem(KEY_HAS_DOB_VIOLATION, '1');
  }

  // Prefer TTL to birthday, as it uses the server clock
  // Store the birthdate (if available) so we can calculate the days left on app init
  if (ttl) {
    const daysLeft = Math.ceil(ttl / ONE_DAY);
    const birthdateFromTtl = getDateFromTimeRemaining(ttl, 18);
    yield* put(setDobViolationDaysLeft(daysLeft));
    localStorage.setItem(KEY_DOB_VIOLATION_BIRTHDATE, birthdateFromTtl.toString());
  } else if (birthdate) {
    yield* put(setDobViolationDaysLeft(getDaysLeft(new Date(birthdate), 18)));
    localStorage.setItem(KEY_DOB_VIOLATION_BIRTHDATE, birthdate);
  }
}

export function* deviceUnflaggedForDobViolationSaga() {
  // Clear the violation on the device/session
  localStorage.setItem(KEY_DOB_VIOLATION_BIRTHDATE, '');
  localStorage.setItem(KEY_HAS_DOB_VIOLATION, '');

  executeEvent(I13N_TYPE_FEATURE_COUNT, {
    category: I13N_CATEGORY_APP,
    action: I13N_ACTION_DOB_DEVICE_UNFLAGGED,
    label: 'DOB Violation',
    message: 'Unflagged',
  });

  // Clear the timeout in the reducer
  yield* put(setDobViolationDaysLeft(0));
}

function* registerDOBSaga() {
  yield* all([
    takeEvery(DEVICE_FLAGGED_FOR_DOB_VIOLATION, deviceFlaggedForDobViolationSaga),
    takeEvery(DEVICE_UNFLAGGED_FOR_DOB_VIOLATION, deviceUnflaggedForDobViolationSaga),
  ]);
}

export function* handleDOBViolation() {
  yield* spawn(registerDOBSaga);

  const birthdate = localStorage.getItem(KEY_DOB_VIOLATION_BIRTHDATE);
  const hasDobViolation = localStorage.getItem(KEY_HAS_DOB_VIOLATION);

  if (hasDobViolation) {
    if (!birthdate) {
      yield* put(deviceFlaggedForDobViolation());
    } else {
      const daysLeft = getDaysLeft(new Date(birthdate), 18);

      if (daysLeft === 0 || (daysLeft && daysLeft < 0)) {
        // If we've reached the timeout, then the user should be of-age, and
        // we can allow them to log in again

        // Clear the flag in the reducer
        yield* put(deviceUnflaggedForDobViolation());
      } else {
        // Device user is still underage, so we update the days left based on
        // the entered birthday (if there is one)
        yield* put(deviceFlaggedForDobViolation(birthdate));
      }
    }
  }
}
